import { supabase } from '../lib/supabase';
import { applyDomainFilterToQuery } from '../hooks/useDomainQuery';

export interface ArtWork {
  id: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  featured_image_url: string;
  created_at: string;
  updated_at: string;
  categories?: { id: string; name: string }[];
  materials?: string[];
  dimensions?: string;
  location?: string;
  year?: string;
  gallery_images?: string[];
  making_of_images?: string[];
  making_of?: string;
  status?: string;
  tenant_id?: string;
}

export interface ArtCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  artwork_count?: number;
  domain?: string;
  created_at?: string;
}

/**
 * Récupère toutes les œuvres du portfolio
 */
export const fetchArtworks = async (categoryId?: string, searchQuery?: string) => {
  try {
    // Récupérer les œuvres avec logique de domaine (fallback domaine → tenant_id)
    let query = supabase
      .from('portfolio_artworks')
      .select(`
        id,
        title,
        slug,
        description,
        content,
        featured_image_url,
        materials,
        dimensions,
        location,
        year,
        gallery_images,
        making_of_images,
        status,
        created_at,
        updated_at
      `)
      .eq('status', 'published');

    // Appliquer le filtre domaine/tenant_id
    query = applyDomainFilterToQuery(query);

    query = query.order('created_at', { ascending: false });

    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }

    const { data: artworks, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des œuvres:', error);
      throw error;
    }

    // Filtrer par catégorie si nécessaire
    let filteredArtworks = artworks || [];

    if (categoryId) {
      // Récupérer les IDs des œuvres associées à la catégorie
      const { data: artworkCategories, error: categoriesError } = await supabase
        .from('portfolio_artwork_categories')
        .select('artwork_id')
        .eq('category_id', categoryId);

      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        throw categoriesError;
      }

      // Filtrer les œuvres par ID
      const artworkIds = artworkCategories?.map(pc => pc.artwork_id) || [];
      filteredArtworks = filteredArtworks.filter(artwork => artworkIds.includes(artwork.id));
    }

    // Récupérer les catégories pour chaque œuvre
    const formattedArtworks = await Promise.all(filteredArtworks.map(async (artwork) => {
      const { data: categories, error: categoriesError } = await supabase
        .from('portfolio_artwork_categories')
        .select(`
          category:portfolio_categories(id, name)
        `)
        .eq('artwork_id', artwork.id);

      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        return {
          ...artwork,
          categories: []
        };
      }

      return {
        ...artwork,
        categories: categories?.map((cat: any) => cat.category) || [],
        materials: Array.isArray(artwork.materials) ? artwork.materials : [],
        gallery_images: Array.isArray(artwork.gallery_images) ? artwork.gallery_images : [],
        making_of_images: Array.isArray(artwork.making_of_images) ? artwork.making_of_images : []
      };
    }));

    return formattedArtworks;
  } catch (error) {
    console.error('Erreur lors de la récupération des œuvres:', error);
    throw error;
  }
};

/**
 * Récupère une œuvre par son slug
 */
export const fetchArtworkBySlug = async (slug: string) => {
  try {
    // Récupérer l'œuvre
    const { data: artwork, error } = await supabase
      .from('portfolio_artworks')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      console.error('Erreur lors de la récupération de l\'œuvre:', error);
      throw error;
    }

    if (!artwork) {
      return null;
    }

    // Incrémenter le compteur de vues
    const { error: updateError } = await supabase
      .from('portfolio_artworks')
      .update({ views_count: (artwork.views_count || 0) + 1 })
      .eq('id', artwork.id);

    if (updateError) {
      console.error('Erreur lors de la mise à jour du compteur de vues:', updateError);
    }

    // Récupérer les catégories de l'œuvre
    const { data: categories, error: categoriesError } = await supabase
      .from('portfolio_artwork_categories')
      .select(`
        category:portfolio_categories(id, name)
      `)
      .eq('artwork_id', artwork.id);

    if (categoriesError) {
      console.error('Erreur lors de la récupération des catégories:', categoriesError);
    }

    return {
      ...artwork,
      categories: categories?.map((cat: any) => cat.category) || [],
      materials: Array.isArray(artwork.materials) ? artwork.materials : [],
      gallery_images: Array.isArray(artwork.gallery_images) ? artwork.gallery_images : [],
      making_of_images: Array.isArray(artwork.making_of_images) ? artwork.making_of_images : []
    };
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'œuvre:', error);
    throw error;
  }
};

/**
 * Récupère toutes les catégories d'œuvres
 */
export const fetchArtCategories = async () => {
  try {
    // Récupérer les catégories avec logique de domaine (fallback domaine → tenant_id)
    let query = supabase
      .from('portfolio_categories')
      .select('*')
      .order('name');

    // Appliquer le filtre domaine/tenant_id
    query = applyDomainFilterToQuery(query);

    const { data, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }

    // Récupérer le nombre d'œuvres par catégorie
    const categoriesWithCount = await Promise.all(
      (data || []).map(async (category) => {
        const { count, error: countError } = await supabase
          .from('portfolio_artwork_categories')
          .select('*', { count: 'exact', head: true })
          .eq('category_id', category.id);

        if (countError) {
          console.error('Erreur lors du comptage des œuvres:', countError);
          return { ...category, artwork_count: 0 };
        }

        return { ...category, artwork_count: count || 0 };
      })
    );

    return categoriesWithCount;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    throw error;
  }
};
